import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { alpacaService, Account, Position } from '../../services/AlpacaService';
import { useAccountMode } from '../../context/AccountModeContext';
import { useDemo } from '../../context/DemoContext';
import { useWebSocket } from '../../context/WebSocketContext';
import { DEMO_ACCOUNT, DEMO_POSITIONS } from '../../services/demoData';
import SignalLoader from '../../components/SignalLoader/SignalLoader';
import BrokerAuthExpiredCard from '../../components/BrokerAuthExpiredCard/BrokerAuthExpiredCard';
import styles from './PnlMonitor.module.css';

const PnlMonitor: React.FC = () => {
  const { mode } = useAccountMode();
  const { isDemoMode, exitDemo } = useDemo();
  const navigate = useNavigate();
  const { connected, on } = useWebSocket();
  const [account, setAccount] = useState<Account | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [brokerAuthExpired, setBrokerAuthExpired] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    if (isDemoMode) {
      setAccount(DEMO_ACCOUNT);
      setPositions(DEMO_POSITIONS);
      setLastUpdated(new Date());
      setLoading(false);
      return;
    }
    try {
      const [acct, pos] = await Promise.all([alpacaService.getAccount(mode), alpacaService.getPositions(mode)]);
      setAccount(acct);
      setPositions(pos.positions);
      setLastUpdated(new Date());
      setError('');
      setBrokerAuthExpired(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
      setBrokerAuthExpired(Boolean(err?.brokerAuthExpired));
    } finally {
      setLoading(false);
    }
  }, [mode, isDemoMode]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, connected ? 60000 : 30000);
    return () => clearInterval(interval);
  }, [fetchData, connected]);

  // WebSocket: instant refresh on position changes
  useEffect(() => {
    const unsub1 = on('position_update', () => fetchData());
    const unsub2 = on('order_fill', () => fetchData());
    return () => {
      unsub1();
      unsub2();
    };
  }, [on, fetchData]);

  const fmt = (val: string | undefined, decimals = 2) => {
    const num = parseFloat(val || '0');
    return num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const pnlClass = (val: string | undefined) => {
    const num = parseFloat(val || '0');
    return num > 0 ? styles.positive : num < 0 ? styles.negative : '';
  };

  if (loading) {
    return (
      <section className={styles.page}>
        <SignalLoader message="Loading account data..." fullPage />
      </section>
    );
  }

  if (error) {
    if (brokerAuthExpired) {
      return (
        <section className={styles.page}>
          <BrokerAuthExpiredCard mode={mode} variant="full" />
        </section>
      );
    }
    return (
      <section className={styles.page}>
        <p className={styles.error}>{error}</p>
        <Link to="/home" className={styles.back}>
          ← Back to Home
        </Link>
      </section>
    );
  }

  return (
    <section className={styles.page}>
      {isDemoMode && (
        <div
          style={{
            background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
            padding: '10px 20px',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ color: '#fff', fontSize: '0.9rem' }}>
            You're viewing demo data — sign up and connect your broker for live trading
          </span>
          <button
            onClick={() => {
              exitDemo();
              navigate('/onboarding');
            }}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.3)',
              padding: '6px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem',
            }}
          >
            Exit Demo
          </button>
        </div>
      )}
      <div className={styles.header}>
        <h1 className={styles.title}>📊 PnL Monitor</h1>
        {lastUpdated && <span className={styles.updated}>Last updated: {lastUpdated.toLocaleTimeString()}</span>}
      </div>

      {account && (
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Equity</span>
            <span className={styles.statValue}>${fmt(account.equity)}</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Daily P&L</span>
            <span className={`${styles.statValue} ${pnlClass(account.daily_pnl)}`}>
              ${fmt(account.daily_pnl)} ({account.daily_pnl_pct}%)
            </span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Buying Power</span>
            <span className={styles.statValue}>${fmt(account.buying_power)}</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Cash</span>
            <span className={styles.statValue}>${fmt(account.cash)}</span>
          </div>
        </div>
      )}

      <h2 className={styles.sectionTitle}>Open Positions</h2>
      {positions.length === 0 ? (
        <p className={styles.empty}>No open positions</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Side</th>
                <th>Qty</th>
                <th>Entry</th>
                <th>Current</th>
                <th>Market Value</th>
                <th>Unrealized P&L</th>
                <th>P&L %</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((pos) => (
                <tr key={pos.symbol}>
                  <td className={styles.symbol}>{pos.symbol}</td>
                  <td className={pos.side === 'long' ? styles.long : styles.short}>{pos.side.toUpperCase()}</td>
                  <td>{pos.qty}</td>
                  <td>${fmt(pos.avg_entry_price)}</td>
                  <td>${fmt(pos.current_price)}</td>
                  <td>${fmt(pos.market_value)}</td>
                  <td className={pnlClass(pos.unrealized_pl)}>${fmt(pos.unrealized_pl)}</td>
                  <td className={pnlClass(pos.unrealized_plpc)}>
                    {fmt(String(parseFloat(pos.unrealized_plpc || '0') * 100))}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Link to="/home" className={styles.back}>
        ← Back to Home
      </Link>
    </section>
  );
};

export default PnlMonitor;
